import { z } from 'zod';
import { uuidSchema as uuid } from './schemas';

/**
 * Photos et documents d'un dossier (CDC §4.7, §4.8, §4.10).
 * Les métadonnées voyagent par la synchronisation (hors ligne), le contenu par envoi
 * progressif en morceaux, avec empreinte SHA-256 vérifiée par le serveur.
 */
export const FILE_KINDS = ['photo', 'document', 'video'] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** Types acceptés : photos, vidéos (MP4 / MOV) et PDF. Le serveur vérifie aussi la signature binaire du contenu. */
export const ALLOWED_FILE_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'video/mp4',
  'video/quicktime',
] as const;
export const VIDEO_MIME = ['video/mp4', 'video/quicktime'] as const;
export const isVideoMime = (m: string) => (VIDEO_MIME as readonly string[]).includes(m);
export type AllowedMime = (typeof ALLOWED_FILE_MIME)[number];
export const FILE_MIME_LABELS: Record<AllowedMime, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/heic': 'HEIC',
  'image/heif': 'HEIF',
  'application/pdf': 'PDF',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
};

/** taille d'un morceau d'envoi (le serveur accepte jusqu'à cette taille par requête) */
export const FILE_CHUNK_BYTES = 1024 * 1024;
/** limite par défaut (le serveur applique FILE_MAX_BYTES) */
export const FILE_MAX_BYTES_DEFAULT = 25 * 1024 * 1024;
/** vidéos : limite par défaut (le serveur applique FILE_MAX_VIDEO_BYTES) et durée maximale à la prise de vue */
export const FILE_MAX_VIDEO_BYTES_DEFAULT = 100 * 1024 * 1024;
export const VIDEO_MAX_SECONDS = 120;

/** listes administrables utilisées pour classer les fichiers */
export const PHOTO_CATEGORY = 'photo_category';
export const DOCUMENT_TYPE = 'document_type';

export const fileCreateInput = z
  .object({
    id: uuid,
    dossierId: uuid,
    kind: z.enum(FILE_KINDS),
    categoryId: uuid.nullish().transform((v) => v ?? null),
    name: z
      .string()
      .trim()
      .max(200)
      .nullish()
      .transform((v) => v ?? ''),
    originalFilename: z
      .string()
      .trim()
      .max(255)
      .nullish()
      .transform((v) => v || null),
    mime: z.enum(ALLOWED_FILE_MIME, { message: 'Type de fichier non accepté (photo, vidéo MP4/MOV ou PDF)' }),
    sizeBytes: z.number().int().positive('Fichier vide'),
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'Empreinte invalide'),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'document' && !v.name)
      ctx.addIssue({ code: 'custom', path: ['name'], message: 'Nom du document obligatoire' });
    if (v.kind === 'photo' && !v.mime.startsWith('image/'))
      ctx.addIssue({ code: 'custom', path: ['mime'], message: 'Une photo doit être une image' });
    if (v.kind === 'video' && !isVideoMime(v.mime))
      ctx.addIssue({ code: 'custom', path: ['mime'], message: 'Une vidéo doit être au format MP4 ou MOV' });
    if (v.kind !== 'video' && isVideoMime(v.mime))
      ctx.addIssue({ code: 'custom', path: ['kind'], message: 'Une vidéo doit être ajoutée comme vidéo' });
  });
export type FileCreateInput = z.input<typeof fileCreateInput>;
export const FILE_MUTABLE_FIELDS = ['name', 'categoryId'] as const;

export interface DossierFileRecord {
  id: string;
  version: number;
  updatedAt: string;
  dossierId: string;
  kind: FileKind;
  categoryId: string | null;
  name: string;
  originalFilename: string | null;
  mime: AllowedMime;
  sizeBytes: number;
  sha256: string;
  /** pending = contenu pas encore entièrement reçu par le serveur */
  status: 'pending' | 'ready';
  source: 'app' | 'portal';
  createdBy: string | null;
  createdByName: string;
  createdAt: string;
}

/** Limite applicable selon le type de fichier. */
export const maxBytesFor = (
  kind: FileKind,
  limits: { file: number; video: number } = {
    file: FILE_MAX_BYTES_DEFAULT,
    video: FILE_MAX_VIDEO_BYTES_DEFAULT,
  },
) => (kind === 'video' ? limits.video : limits.file);

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
}

/** Hex SHA-256 via WebCrypto (navigateur, Node ≥ 20). Le mobile fournit sa propre implémentation. */
export async function sha256Hex(data: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Origine d'une photo du dossier : « poseur » si elle a été prise par un technicien d'une intervention du dossier
 * (principal, renfort, ou celui qui l'a terminée) ; « commerciale » sinon (visite, vente). Profil mixte (commercial
 * et poseur du même dossier) : poseur seulement à partir de la prise en charge de l'intervention.
 */
export type PhotoOrigin = 'sale' | 'install';
export function photoOriginOf(
  f: { createdBy: string | null; createdAt: string },
  interventions: readonly { technicianId: string | null; coTechnicianIds?: string[]; completedById?: string | null; takenChargeAt?: string | null; onSiteAt?: string | null }[],
  commercialIds: readonly string[] = [],
): PhotoOrigin {
  if (!f.createdBy) return 'sale';
  const installers = new Set(interventions.flatMap((i) => [i.technicianId, i.completedById, ...(i.coTechnicianIds ?? [])]).filter((x): x is string => !!x));
  if (!installers.has(f.createdBy)) return 'sale';
  if (!commercialIds.includes(f.createdBy)) return 'install';
  const start = interventions
    .map((i) => i.takenChargeAt ?? i.onSiteAt)
    .filter((x): x is string => !!x)
    .sort()[0];
  return start && f.createdAt >= start ? 'install' : 'sale';
}
